import type { Chat } from '@trinity/chat';
import { ChatResponseType } from '@trinity/chat';
import { markdownToRawText } from './markdown';

class UnreachableCaseError extends Error {
  constructor(val: never) {
    super(`Unreachable case: ${JSON.stringify(val)}`);
  }
}

export function getSegmentationQueryResponseAsText(
  results: Array<{
    dimension: string;
    findings: Array<{
      label: string;
      response: Chat.QueryResponse;
    }>;
  }>
): string {
  let buffer = '';

  for (const result of results) {
    buffer += `${result.dimension}\n\n`;
    for (const finding of result.findings) {
      buffer += `${finding.label}:\n\n`;
      const text = getQueryResponseAsText(finding.response);
      buffer += `${text}\n\n`;
    }
    buffer += `\n`;
  }

  return buffer;
}

const MARKDOWN_RESPONSE_TYPES = new Set<ChatResponseType>([
  ChatResponseType.Markdown,
  ChatResponseType.MarkdownHtml,
  ChatResponseType.MarketingMessage,
  ChatResponseType.LocationRouting,
  ChatResponseType.BIFindQuotes,
  ChatResponseType.BIFindQuantAnalyses,
]);

const EMPTY_RESPONSE_TYPES = new Set<ChatResponseType>([
  ChatResponseType.HcpAvailability,
  ChatResponseType.HcpSearch,
  ChatResponseType.HcpSearchResults,
  ChatResponseType.HcpRecommendationsSearch,
  ChatResponseType.HcpSummary,
  ChatResponseType.HcpSelection,
  ChatResponseType.LocationSelection,
  ChatResponseType.SchedulingAssistant,
  ChatResponseType.SmartRouting,
  ChatResponseType.SegmentChatNeeded,
  ChatResponseType.SegmentChatCancelled,
  ChatResponseType.SegmentChatSelected,
]);

const VALUE_RESPONSE_TYPES = new Set<ChatResponseType>([
  ChatResponseType.FormWorkflow,
  ChatResponseType.SegmentationTagsNeeded,
  ChatResponseType.SegmentationQuestionsNeeded,
  ChatResponseType.SegmentationComplete,
  ChatResponseType.SegmentationCancelled,
  ChatResponseType.SegmentationUpload,
  ChatResponseType.SegmentationConfigure,
  ChatResponseType.SegmentationEDA,
  ChatResponseType.SegmentationStrategy,
  ChatResponseType.SegmentationRefinement,
  ChatResponseType.SegmentationFeatures,
  ChatResponseType.SegmentationResults,
  ChatResponseType.SegmentationExport,
]);

export function getQueryResponseAsText(response: Chat.QueryResponse): string {
  if (response.type === ChatResponseType.Text) return response.value;

  if (MARKDOWN_RESPONSE_TYPES.has(response.type)) {
    return markdownToRawText((response as { value: string }).value);
  }

  if (EMPTY_RESPONSE_TYPES.has(response.type)) return '';

  if (response.type === ChatResponseType.Chart) return response.data.chart.response;
  if (response.type === ChatResponseType.BIChartQuantAnalyses) return response.data.chart.response;

  if (VALUE_RESPONSE_TYPES.has(response.type)) {
    return (response as { value: string }).value;
  }

  throw new UnreachableCaseError(response as never);
}
